import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, Gift, CreditCard, ArrowClockwise } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import ContextHelpButton from './ContextHelpButton';

const COPY = {
  it: {
    eyebrow: 'Bonus Creator',
    title: '7 giorni di Strategist, quando vuoi tu.',
    body: 'Il tuo abbonamento resta Creator da 25€/mese. Attivi una sola volta 7 giorni di accesso Strategist e, alla scadenza, torni automaticamente a Creator senza addebiti extra.',
    activate: 'Attiva 7 giorni gratis',
    activating: 'Attivazione…',
    activeTitle: 'Trial Strategist attivo',
    activeBody: 'Hai accesso alle funzioni Strategist fino al {{date}}. Il tuo piano pagato resta Creator finché non scegli di fare upgrade.',
    upgrade: 'Resta Strategist · 49€/mese',
    upgrading: 'Apertura pagamento…',
    used: 'La prova Strategist gratuita è già stata utilizzata su questo account.',
    loadError: 'Non riesco a verificare la prova Strategist in questo momento.',
    retry: 'Riprova',
    help: 'Guida prova Strategist',
  },
  en: {
    eyebrow: 'Creator bonus',
    title: '7 days of Strategist, whenever you want.',
    body: 'Your subscription stays Creator at €25/month. You can activate one 7-day Strategist access period and automatically return to Creator when it ends, with no extra charge.',
    activate: 'Start 7 free days',
    activating: 'Activating…',
    activeTitle: 'Strategist trial active',
    activeBody: 'You have Strategist access until {{date}}. Your paid plan remains Creator unless you choose to upgrade.',
    upgrade: 'Keep Strategist · €49/month',
    upgrading: 'Opening checkout…',
    used: 'The free Strategist trial has already been used on this account.',
    loadError: 'Strategist trial status is unavailable right now.',
    retry: 'Retry',
    help: 'Strategist trial guide',
  },
  es: {
    eyebrow: 'Bonus Creator',
    title: '7 días de Strategist, cuando tú quieras.',
    body: 'Tu suscripción sigue siendo Creator por 25 €/mes. Puedes activar una sola vez 7 días de acceso Strategist y volverás automáticamente a Creator al terminar, sin cargos extra.',
    activate: 'Activar 7 días gratis',
    activating: 'Activando…',
    activeTitle: 'Prueba Strategist activa',
    activeBody: 'Tienes acceso Strategist hasta el {{date}}. Tu plan de pago sigue siendo Creator hasta que decidas actualizarlo.',
    upgrade: 'Quedarme con Strategist · 49 €/mes',
    upgrading: 'Abriendo pago…',
    used: 'La prueba gratuita de Strategist ya se utilizó en esta cuenta.',
    loadError: 'Ahora mismo no se puede comprobar la prueba Strategist.',
    retry: 'Reintentar',
    help: 'Guía de la prueba Strategist',
  },
  fr: {
    eyebrow: 'Bonus Creator',
    title: '7 jours de Strategist, quand vous voulez.',
    body: 'Votre abonnement reste Creator à 25 €/mois. Vous pouvez activer une seule fois 7 jours d’accès Strategist puis revenir automatiquement à Creator, sans frais supplémentaires.',
    activate: 'Activer 7 jours gratuits',
    activating: 'Activation…',
    activeTitle: 'Essai Strategist actif',
    activeBody: 'Vous avez accès à Strategist jusqu’au {{date}}. Votre offre payante reste Creator tant que vous ne choisissez pas de passer à Strategist.',
    upgrade: 'Garder Strategist · 49 €/mois',
    upgrading: 'Ouverture du paiement…',
    used: 'L’essai gratuit Strategist a déjà été utilisé sur ce compte.',
    loadError: 'Impossible de vérifier l’essai Strategist pour le moment.',
    retry: 'Réessayer',
    help: 'Guide de l’essai Strategist',
  },
};

function interpolate(text, values = {}) {
  return String(text || '').replace(/\{\{(\w+)\}\}/g, (_, key) => values[key] ?? '');
}

export default function StrategistTrialCard() {
  const { api, user, basePlan, refreshPlanLimits, refreshEffectivePlan } = useAuth();
  const { i18n } = useTranslation();
  const language = String(i18n?.resolvedLanguage || i18n?.language || 'it').split('-')[0];
  const copy = COPY[language] || COPY.it;
  const paidPlan = basePlan || user?.plan || 'free';
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    if (paidPlan !== 'creator') return;
    setError(false);
    api.get('/billing/strategist-trial')
      .then(({ data }) => setStatus(data || {}))
      .catch(() => setError(true));
  }, [api, paidPlan]);

  useEffect(() => { load(); }, [load]);

  const formattedExpiry = useMemo(() => {
    if (!status?.expires_at) return '';
    const date = new Date(status.expires_at);
    if (Number.isNaN(date.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
    } catch {
      return date.toLocaleString();
    }
  }, [status?.expires_at, language]);

  if (paidPlan !== 'creator') return null;

  const activate = async () => {
    setBusy('activate');
    setError(false);
    try {
      await api.post('/billing/strategist-trial/activate');
      await Promise.all([refreshPlanLimits?.(), refreshEffectivePlan?.()]);
      load();
    } catch {
      setError(true);
    } finally {
      setBusy('');
    }
  };

  const upgrade = async () => {
    setBusy('upgrade');
    setError(false);
    try {
      const { data } = await api.post('/billing/upgrade-checkout', {
        plan_id: 'strategist',
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
    } catch {
      setError(true);
      setBusy('');
    }
  };

  return (
    <div
      className="card mb-8 overflow-hidden"
      data-testid="strategist-trial-card"
      style={{ border: '1px solid rgba(168,85,247,.32)', background: 'linear-gradient(135deg, rgba(99,102,241,.10), rgba(236,72,153,.07))' }}
    >
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white">
          {status?.active ? <Crown size={23} weight="fill" /> : <Gift size={23} weight="fill" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[.18em] text-fuchsia-300">{copy.eyebrow}</p>
              <h3 className="mt-1 text-lg font-bold">{status?.active ? copy.activeTitle : copy.title}</h3>
            </div>
            <ContextHelpButton articleId="strategist-trial" label={copy.help} />
          </div>
          <p className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">
            {status?.active ? interpolate(copy.activeBody, { date: formattedExpiry }) : copy.body}
          </p>

          {error && (
            <div className="mt-3 flex items-center gap-2 text-xs text-rose-300">
              <span>{copy.loadError}</span>
              <button type="button" className="underline" onClick={load}><ArrowClockwise size={12} className="inline mr-1" />{copy.retry}</button>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            {status?.active ? (
              <button type="button" className="btn-gradient flex items-center gap-2" onClick={upgrade} disabled={Boolean(busy)} data-testid="strategist-trial-upgrade">
                <CreditCard size={15} /> {busy === 'upgrade' ? copy.upgrading : copy.upgrade}
              </button>
            ) : status?.eligible ? (
              <button type="button" className="btn-gradient flex items-center gap-2" onClick={activate} disabled={Boolean(busy)} data-testid="strategist-trial-activate">
                <Crown size={15} /> {busy === 'activate' ? copy.activating : copy.activate}
              </button>
            ) : status?.used ? (
              <p className="text-xs text-[var(--text-muted)]">{copy.used}</p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
